import { useState, useRef, useCallback } from "react";

interface UseResizablePanelReturn {
  leftPanelWidth: number;
  isResizing: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  resizeDividerRef: React.RefObject<HTMLDivElement>;
  handleMouseDown: () => void;
}

export const useResizablePanel = (): UseResizablePanelReturn => {
  // Start with a compact but readable chat panel (35% instead of 25%)
  const [leftPanelWidth, setLeftPanelWidth] = useState(35);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeDividerRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const mouseX = e.clientX - containerRect.left;

    // Calculate percentage with constraints for a compact but resizable chat panel
    let newLeftWidth = (mouseX / containerWidth) * 100;

    // Set minimum chat width to 20% and maximum to 60% to keep it adjustable but start compact
    const minChatWidthPercent = 20;
    const maxChatWidthPercent = 60;

    newLeftWidth = Math.max(
      minChatWidthPercent,
      Math.min(maxChatWidthPercent, newLeftWidth)
    );

    setLeftPanelWidth(newLeftWidth);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  }, [handleMouseMove]);

  const handleMouseDown = useCallback(() => {
    setIsResizing(true);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [handleMouseMove, handleMouseUp]);

  return {
    leftPanelWidth,
    isResizing,
    containerRef,
    resizeDividerRef,
    handleMouseDown,
  };
};
